import React from "react";
import { useTranslation } from "react-i18next";
import UserSettingsService from "src/services/userSettingsService";
import { makeStyles } from "tss-react/mui";

import { Menu } from "@devices/components/Header/Menu";
import { Box, Theme, Typography } from "@mui/material";
import { CheckboxField } from "@shared/form/CheckboxField";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import { Table } from "@tanstack/react-table";

import DropdownContainer from "../shared/Dropdown/DropdownContainer";

import { DeviceTableData } from "./DeviceTable.utils";

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        display: "flex",
        marginBottom: theme.spacing(2),
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    box: {
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(1),
    },
    dropdownBox: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "space-between",
        width: "100%",
        padding: theme.spacing(3, 5),
    },
    checkboxContainer: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(1),
    },
    title: {
        display: "inline",
        fontWeight: 700,
    },
    titleContainer: {
        textAlign: "center",
        marginBottom: theme.spacing(1),
    },
    outerContainer: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
}));

type Props = {
    value: string;
    onChange: (value: string) => void;
    setVisibleColumns: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    onClear: () => void;
    table: Table<DeviceTableData>;
};
export const ManagementSection = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const anchorRefVisibility = React.useRef(null);

    // exclude actions columns from the list of columns where visibility can be toggled
    const tableColumns = props.table.getAllColumns().filter((column) => column.columnDef.id !== "moveLicenseCheckbox");
    const [visibleColumns, setVisibleColumns] = React.useState(
        Object.fromEntries(
            tableColumns.flatMap((column) =>
                column.getLeafColumns().map((leafColumn) => [leafColumn.id, leafColumn.getIsVisible()]),
            ),
        ),
    );

    const handleColumnVisibilityChange = (id: string) => {
        setVisibleColumns((prevVisibleColumns) => {
            const newVisibilityState = {
                ...prevVisibleColumns,
                [id]: prevVisibleColumns[id] !== undefined ? !prevVisibleColumns[id] : false,
            };

            return newVisibilityState;
        });
    };
    const onClickAway = () => {
        props.setVisibleColumns(visibleColumns);
        UserSettingsService.setWorkspaceValue("devicePage.visibleColumns", visibleColumns);
    };

    return (
        <Box className={classes.outerContainer}>
            <Box className={classes.container}>
                <QueryBuilderField
                    value={props.value}
                    placeholder={t("common:button.search")}
                    onChange={props.onChange}
                    onClear={props.onClear}
                    options={[
                        { key: "device.lastState.build.model", label: t("pages:devicePage.filters.deviceModel") },
                        { key: "groupName", label: t("pages:devicePage.filters.groupName") },
                        { key: "device.serial", label: t("pages:devicePage.filters.serial") },
                        { key: "device.lastState.packages.versionCode", label: t("pages:devicePage.filters.applicationVersion") },
                        { key: "device.lastState.packages.package", label: t("pages:devicePage.filters.applicationPackage") },
                        { key: "chargeId", label: t("pages:devicePage.filters.chargeId") },
                    ]}
                />

                <Box className={classes.box}>
                    <Menu />
                    <DropdownContainer
                        label={t("pages:devicePage.button.columns")}
                        icon={<i className="fas fa-tasks" />}
                        anchorRef={anchorRefVisibility}
                        listProps={{ onClickAway, eventType: "onKeyDown" }}
                    >
                        <Box className={classes.dropdownBox}>
                            {tableColumns.map((column) => (
                                <Box key={column.id}>
                                    <div className={classes.titleContainer}>
                                        <Typography variant="body1" className={classes.title}>
                                            {column.id}
                                        </Typography>
                                    </div>
                                    {column.getLeafColumns().map((leafColumn) => (
                                        <Box className={classes.checkboxContainer}>
                                            <CheckboxField
                                                checked={visibleColumns[leafColumn.id]}
                                                onChange={() => handleColumnVisibilityChange(leafColumn.id)}
                                                label={(leafColumn.columnDef.header as string) || "Indicator"}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </DropdownContainer>
                </Box>
            </Box>
        </Box>
    );
};
